import React, { useEffect, useState, useRef } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'common/i18n/hooks';
import { fetchUsers } from 'common/report/queries/query-api/users-client';
import { UserSearchTagInputReact } from 'common/widgets/sharing/modal/user-search-tag-input';
import { SelectReact } from 'common/components/select';
import { SelectTheme } from 'common/components/select/constants';
import { Cohort } from 'common/query-builder/cohorts-query-builder/models/cohort';
import { debounce } from 'common/util/lodash-wrapped';
import { TagInputChangeAction } from 'common/components/tag-input/types';
import { TagItem } from 'common/components/tag-input/tag-item';
import { SpinnerReact } from 'common/components/spinner';
import styles from './index.module.styl';
export interface UserDisplay {
  email: string;
  distinctId?: string;
  variant: string;
}
interface FeatureFlagTestUsersProps {
  testUsers: UserDisplay[];
  variantList: string[];
  isLoadingTestUsers?: boolean;
  handleAdd: (TestUsers: UserDisplay[]) => void;
  handleChangeVariant: (distinctId: string, variant: string, remove: boolean) => void;
  canUpdate?: boolean;
}
const handleInput = debounce(myFunc => {
  myFunc();
}, 200, {
  trailing: true
});
const emailReg = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
export function FeatureFlagTestUsers(_ref: FeatureFlagTestUsersProps) {
  let {
    canUpdate = false,
    testUsers,
    variantList,
    handleAdd,
    handleChangeVariant,
    isLoadingTestUsers
  }: FeatureFlagTestUsersProps = _ref;
  const i18n = useTranslation();
  const [users, setUsers] = useState<TODO>([]);
  const [searchString, setSearchString] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [tags, setTags] = useState<TagItem[]>([]);
  const [selectedVariant, setSelectedVariant] = useState<TODO>({
    value: -1,
    label: i18n.t("feature-flags.page.testers.auto")
  });
  const fetchUserAbortSignal = useRef<AbortController>(new AbortController());
  useEffect(() => {
    async function callFetchUsers() {
      fetchUserAbortSignal.current.abort();
      fetchUserAbortSignal.current = new AbortController();
      await fetchUsers({
        abortSignal: fetchUserAbortSignal.current.signal,
        cohort: new Cohort(),
        sortColumn: {
          value: "$email",
          resourceType: "user"
        },
        includeAllUsers: true,
        search: searchString,
        limit: 1000,
        columns: [{
          value: "$email",
          resourceType: "user"
        }, {
          value: "$distinct_id",
          resourceType: "user"
        }, {
          value: "$name",
          resourceType: "user"
        }],
        sortOrder: "ascending"
      }).then(response => {
        setUsers(response);
        setIsLoading(false);
      }).catch(_err => {});
    }
    try {
      callFetchUsers();
    } catch (_err) {
      // TODO: Error toast
    }
  }, [searchString]);
  function userToSectionItem(user: TODO) {
    return {
      label: "$email" in user.$properties ? user.$properties["$email"] : user.$distinct_id,
      value: user.$distinct_id,
      isDisabled: !!testUsers.find(t => t.distinctId === user.$distinct_id) || !!tags.find(tag => tag.value === user.$distinct_id)
    };
  }
  const userGroups = Object.groupBy(users, (user: TODO) => "$email" in user.$properties || emailReg.test(user.$distinct_id.trim()) ? "email" : "distinct_id");
  const menuSections = !isLoading ? [{
    isLoading,
    loadOnSearch: true,
    label: i18n.t("feature-flags.page.testers.users"),
    items: "email" in userGroups && !!userGroups["email"] ? [...userGroups["email"].map(userToSectionItem)].sort((a, b) => a.label.trim().localeCompare(b.label.trim())) : []
  }, {
    items: "distinct_id" in userGroups && !!userGroups["distinct_id"] ? [...userGroups["distinct_id"].map(userToSectionItem)].sort((a, b) => a.label.trim().localeCompare(b.label.trim())) : []
  }] : [{
    isLoading,
    loadOnSearch: true,
    label: i18n.t("feature-flags.page.testers.users"),
    items: "distinct_id" in userGroups && !!userGroups["distinct_id"] ? [...userGroups["distinct_id"].map(userToSectionItem)].sort((a, b) => a.label.trim().localeCompare(b.label.trim())) : []
  }];
  return <div className={styles.testers} data-sentry-component="FeatureFlagTestUsers" data-sentry-source-file="index.tsx">
      <h2 className={styles.testersHeader}>{i18n.t("feature-flags.page.testers.title")}</h2>
      <UserSearchTagInputReact openOnRefresh preserveSearchOnTagClick selectedInputOption={selectedVariant.label} label={""} disabled={!canUpdate} picker={{
      handleInputChange: _ref2 => {
        let {
          detail
        } = _ref2;
        const {
          value
        } = detail;
        if (detail.action === TagInputChangeAction.RemoveTag) {
          setTags(tags.filter(tag => (tag === null || tag === void 0 ? void 0 : tag.value) !== detail.tag.value));
        } else {
          setIsLoading(true);
          handleInput(() => {
            setSearchString(value);
          });
        }
      },
      handleItemClick: _ref3 => {
        let {
          detail
        } = _ref3;
        setTags([...tags, detail]);
      },
      handleLevelSelectChange: _ref4 => {
        let {
          detail
        } = _ref4;
        setSelectedVariant(detail.item);
      }
    }} selectOptions={[{
      label: i18n.t("feature-flags.page.testers.variant"),
      items: [{
        label: i18n.t("feature-flags.page.testers.auto"),
        value: -1
      }, ...variantList.map((name, index) => {
        return {
          label: name,
          value: index
        };
      })]
    }]} menuSections={menuSections} cta={{
      text: i18n.t("feature-flags.page.testers.add"),
      disabled: !canUpdate,
      onClick: () => {
        let newTestUsers = testUsers;
        tags.forEach(tag => {
          let curVar = selectedVariant.label;
          if (selectedVariant.value === -1) {
            curVar = variantList[Math.floor(Math.random() * variantList.length)];
          }
          newTestUsers = [...newTestUsers, {
            email: tag.label,
            variant: curVar,
            distinctId: tag.value
          }];
        });
        handleAdd(newTestUsers);
        setTags([]);
      },
      icon: ""
    }} tags={tags} searchText={searchString} placeholder={i18n.t("feature-flags.page.testers.searchPlaceholder")} showPopover data-sentry-element="UserSearchTagInputReact" data-sentry-source-file="index.tsx" />
      {!isLoadingTestUsers && testUsers.map(user => {
      var _user$email;
      return <TestUsersRow key={user.distinctId} testUsername={(_user$email = user.email) !== null && _user$email !== void 0 ? _user$email : user.distinctId} variantList={variantList} variant={user.variant} canUpdate={canUpdate} onChange={(variant, remove) => {
        var _user$distinctId;
        return handleChangeVariant((_user$distinctId = user.distinctId) !== null && _user$distinctId !== void 0 ? _user$distinctId : "", variant, remove);
      }} />;
    })}
      {!!isLoadingTestUsers && <SpinnerReact />}
    </div>;
}
interface TestUsersRowProps {
  testUsername: string;
  variantList: string[];
  variant: string;
  canUpdate: boolean;
  onChange: (variant: string, remove: boolean) => void;
}
function TestUsersRow(_ref5: TestUsersRowProps) {
  let {
    testUsername,
    variant,
    variantList,
    canUpdate,
    onChange
  }: TestUsersRowProps = _ref5;
  const i18n = useTranslation();
  const varIndex = variantList.indexOf(variant);
  return <div className={styles.testersConfigContainer} data-sentry-component="TestUsersRow" data-sentry-source-file="index.tsx">
      <div className={styles.testersConfigRow}>
        <div className={styles.testersBox}>{testUsername}</div>
        <div className={clsx(styles.square, styles["color" + (varIndex === -1 ? 0 : varIndex)])} />
        <div className={styles.variantBox}>
          <SelectReact selectedOption={variant} disabled={!canUpdate} theme={SelectTheme.MenuFilter} onChange={(ev: TODO) => {
          var _ev$detail, _ev$detail2;
          onChange((_ev$detail = ev.detail) === null || _ev$detail === void 0 ? void 0 : _ev$detail.value, ((_ev$detail2 = ev.detail) === null || _ev$detail2 === void 0 ? void 0 : _ev$detail2.item.value) === -1);
        }} menuWidth={-1} sections={[{
          items: [...variantList.map((name, index) => {
            return {
              label: name,
              value: index
            };
          }), {
            label: i18n.t("feature-flags.page.testers.remove"),
            value: -1
          }]
        }]} data-sentry-element="SelectReact" data-sentry-source-file="index.tsx" />
        </div>
      </div>
    </div>;
}